import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { createSubTaskExpense, uploadFileToS3 } from '../../../services/api/implementation/impl';
import Button from '../../../../shared/components/ui/Button';
import { formatRupees } from '../../Management/budget';
import { Plus, Trash2, Paperclip, FileText, X, Info, Loader2 } from 'lucide-react';

const NOTE_MAX = 500;

const labelClass = 'mb-1 block text-xs font-medium text-gray-700';
const inputBase =
    'w-full rounded-md border bg-white px-3 py-1.5 text-sm text-gray-800 focus:outline-none focus:ring-1 transition-colors';
const inputOk = 'border-gray-300 focus:border-[#346C75] focus:ring-[#346C75]';
const inputError = 'border-red-400 focus:border-red-500 focus:ring-red-500';

// Stable row keys, so removing a row never shifts another row's inputs or file picker.
let rowSeq = 0;
const newRow = () => ({
    key: ++rowSeq,
    amount: '',
    paid: false,
    payment_link: '',
    note: '',
    bill: null,
    uploading: false
});

const amountError = (row, wholeRupees) => {
    const n = parseFloat(row.amount);
    if (row.amount === '' || !Number.isFinite(n)) return 'Amount is required';
    if (n <= 0) return 'Amount must be greater than 0';
    if (wholeRupees && !Number.isInteger(n)) return 'Enter whole rupees';
    return null;
};

const noteError = (row) => (row.note.trim() ? null : 'Note is required');

/**
 * Expense lines for a subtask by default. A caller with its own endpoint (a
 * court case) passes `submit(expenses)` instead of `subTaskId`, and may pass
 * `storeName(file)` to choose the name a bill is uploaded under.
 */
const AddExpenseModal = ({
    subTaskId,
    onSuccess,
    onClose,
    draftOnly = false,
    submit = null,
    hint = null,
    wholeRupees = false,
    storeName = null
}) => {
    const [expenses, setExpenses] = useState(() => [newRow()]);
    const [loading, setLoading] = useState(false);
    // Field errors only show once the user has tried to submit.
    const [submitted, setSubmitted] = useState(false);

    const updateRow = (key, patch) => {
        setExpenses((rows) => rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));
    };

    const handleAddRow = () => {
        setExpenses((rows) => [...rows, newRow()]);
    };

    const handleRemoveRow = (key) => {
        setExpenses((rows) => (rows.length > 1 ? rows.filter((row) => row.key !== key) : rows));
    };

    const handleFileUpload = async (key, file) => {
        if (!file) return;
        updateRow(key, { uploading: true });

        try {
            const stored = storeName ? new File([file], storeName(file), { type: file.type }) : file;
            const response = await uploadFileToS3(stored);
            const uploadedFile = response?.data?.files?.[0];
            if (!uploadedFile?.presignedUrl) {
                throw new Error('Invalid upload response');
            }
            updateRow(key, {
                uploading: false,
                bill: { name: file.name, url: uploadedFile.presignedUrl }
            });
            toast.success('Bill uploaded successfully');
        } catch (error) {
            console.error('Upload failed:', error);
            updateRow(key, { uploading: false });
            toast.error('Failed to upload bill');
        }
    };

    const total = expenses.reduce((sum, row) => {
        const n = parseFloat(row.amount);
        return Number.isFinite(n) && n > 0 ? sum + n : sum;
    }, 0);

    const handleSubmit = async () => {
        if (!submit && !subTaskId) {
            toast.error('Subtask ID is missing');
            return;
        }

        setSubmitted(true);
        if (expenses.some((row) => amountError(row, wholeRupees) || noteError(row))) {
            toast.error('Enter an amount and a note for every expense');
            return;
        }

        if (expenses.some((row) => row.uploading)) {
            toast.error('Please wait for file uploads to complete');
            return;
        }

        const lines = expenses.map((row) => {
            const expenseData = {
                amount: parseFloat(row.amount),
                paid: draftOnly ? false : row.paid,
                note: row.note.trim()
            };

            // Only include payment_link if NOT paid
            if (!row.paid && row.payment_link.trim()) {
                expenseData.payment_link = row.payment_link.trim();
            }

            if (row.bill) {
                expenseData.bill = { name: row.bill.name, url: row.bill.url };
            }

            return expenseData;
        });

        setLoading(true);
        try {
            if (submit) {
                await submit(lines);
            } else {
                await createSubTaskExpense({ subTask_id: subTaskId, expenses: lines });
            }
            toast.success('Expenses added successfully');
            onSuccess();
            onClose();
        } catch (error) {
            console.error('Error adding expenses:', error);
            toast.error(error?.response?.data?.message || 'Failed to add expenses');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-3">
            {(hint || draftOnly) && (
                <p className="flex items-center gap-1.5 text-[11px] text-gray-500">
                    <Info size={12} className="shrink-0 text-[#346C75]" />
                    {hint || 'Saved as draft. Approve to send to Accounts.'}
                </p>
            )}

            {expenses.map((expense, index) => {
                const amountMsg = submitted ? amountError(expense, wholeRupees) : null;
                const noteMsg = submitted ? noteError(expense) : null;

                return (
                    <div key={expense.key} className="rounded-lg border border-gray-200 p-3">
                        {expenses.length > 1 && (
                            <div className="mb-2 flex items-center justify-between">
                                <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                    Expense {index + 1}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => handleRemoveRow(expense.key)}
                                    className="flex items-center gap-1 text-xs text-gray-500 transition-colors hover:text-red-600"
                                    aria-label={`Remove expense ${index + 1}`}
                                >
                                    <Trash2 size={13} /> Remove
                                </button>
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-3">
                            <div className={expense.paid ? 'col-span-2' : ''}>
                                <label htmlFor={`expense-amount-${expense.key}`} className={labelClass}>
                                    Amount <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-gray-500">
                                        ₹
                                    </span>
                                    <input
                                        id={`expense-amount-${expense.key}`}
                                        type="number"
                                        inputMode={wholeRupees ? 'numeric' : 'decimal'}
                                        min="0"
                                        step={wholeRupees ? '1' : '0.01'}
                                        value={expense.amount}
                                        onChange={(e) => updateRow(expense.key, { amount: e.target.value })}
                                        onWheel={(e) => e.currentTarget.blur()}
                                        className={`${inputBase} pl-7 ${amountMsg ? inputError : inputOk}`}
                                        placeholder={wholeRupees ? '0' : '0.00'}
                                        aria-invalid={!!amountMsg}
                                    />
                                </div>
                                {amountMsg && <p className="mt-1 text-xs text-red-600">{amountMsg}</p>}
                            </div>

                            {!expense.paid && (
                                <div>
                                    <label htmlFor={`expense-link-${expense.key}`} className={labelClass}>
                                        Payment Link <span className="font-normal text-gray-400">(optional)</span>
                                    </label>
                                    <input
                                        id={`expense-link-${expense.key}`}
                                        type="url"
                                        value={expense.payment_link}
                                        onChange={(e) => updateRow(expense.key, { payment_link: e.target.value })}
                                        className={`${inputBase} ${inputOk}`}
                                        placeholder="https://..."
                                    />
                                </div>
                            )}
                        </div>

                        {!draftOnly && (
                            <label className="mt-2 flex w-fit cursor-pointer items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={expense.paid}
                                    onChange={(e) => updateRow(expense.key, { paid: e.target.checked })}
                                    className="h-4 w-4 rounded border-gray-300 text-[#346C75] focus:ring-[#346C75]"
                                />
                                <span className="text-sm text-gray-700">Already paid</span>
                            </label>
                        )}

                        <div className="mt-3">
                            <div className="flex items-baseline justify-between">
                                <label htmlFor={`expense-note-${expense.key}`} className={labelClass}>
                                    Note <span className="text-red-500">*</span>
                                </label>
                                <span className="text-[11px] text-gray-400">
                                    {expense.note.length}/{NOTE_MAX}
                                </span>
                            </div>
                            <textarea
                                id={`expense-note-${expense.key}`}
                                rows={2}
                                maxLength={NOTE_MAX}
                                value={expense.note}
                                onChange={(e) => updateRow(expense.key, { note: e.target.value })}
                                className={`${inputBase} resize-none ${noteMsg ? inputError : inputOk}`}
                                placeholder="What is this expense for?"
                                aria-invalid={!!noteMsg}
                            />
                            {noteMsg && <p className="mt-1 text-xs text-red-600">{noteMsg}</p>}
                        </div>

                        <div className="mt-3 flex items-center">
                            {expense.bill ? (
                                <div className="flex min-w-0 flex-1 items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1.5">
                                    <FileText size={14} className="shrink-0 text-[#346C75]" />
                                    <a
                                        href={expense.bill.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="min-w-0 flex-1 truncate text-xs text-[#346C75] hover:underline"
                                        title={expense.bill.name}
                                    >
                                        {expense.bill.name}
                                    </a>
                                    <button
                                        type="button"
                                        onClick={() => updateRow(expense.key, { bill: null })}
                                        className="text-gray-400 transition-colors hover:text-red-600"
                                        aria-label="Remove bill"
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            ) : (
                                <label
                                    htmlFor={`expense-bill-${expense.key}`}
                                    className={`inline-flex items-center gap-1.5 rounded-md border border-dashed px-2.5 py-1.5 text-xs transition-colors ${
                                        expense.uploading
                                            ? 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400'
                                            : 'cursor-pointer border-gray-300 text-gray-600 hover:border-[#346C75] hover:bg-[#EBF1F2] hover:text-[#346C75]'
                                    }`}
                                >
                                    {expense.uploading ? (
                                        <>
                                            <Loader2 size={13} className="animate-spin" /> Uploading...
                                        </>
                                    ) : (
                                        <>
                                            <Paperclip size={13} /> Attach bill
                                            <span className="text-gray-400">(optional)</span>
                                        </>
                                    )}
                                </label>
                            )}
                            <input
                                id={`expense-bill-${expense.key}`}
                                type="file"
                                className="hidden"
                                disabled={expense.uploading}
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    // Reset so picking the same file again still fires onChange.
                                    e.target.value = '';
                                    handleFileUpload(expense.key, file);
                                }}
                            />
                        </div>
                    </div>
                );
            })}

            <button
                type="button"
                onClick={handleAddRow}
                className="inline-flex items-center gap-1 text-sm font-medium text-[#346C75] hover:underline"
            >
                <Plus size={15} /> Add another expense
            </button>

            {/* Pinned to the bottom of the modal's scroll area so Submit stays reachable. */}
            <div className="sticky bottom-0 flex items-center justify-between gap-3 border-t border-gray-100 bg-white pt-3">
                <div className="text-sm text-gray-600">
                    Total <span className="font-semibold text-gray-900">{formatRupees(total)}</span>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-lg px-4 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-100"
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <Button onClick={handleSubmit} isLoading={loading} disabled={loading}>
                        {expenses.length > 1 ? `Submit ${expenses.length} expenses` : 'Submit expense'}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default AddExpenseModal;
